import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Button, Dropdown } from "antd";
import {
    ArrowLeftOutlined,
    CloseCircleOutlined,
    DownOutlined,
    FileTextOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    ACTION_SIZE_LG,
    SECONDARY_ACTION,
    formatCreatedMeta,
    getCustomerName,
    getReservationStatus,
} from "../utils";

const STATUS_STYLES = {
    warning: { dot: "bg-[#fa8c16]", text: "text-[#d48806]" },
    processing: { dot: "bg-[#5D5DF6]", text: "text-[#5D5DF6]" },
    success: { dot: "bg-[#52c41a]", text: "text-[#389e0d]" },
    error: { dot: "bg-[#ff4d4f]", text: "text-[#cf1322]" },
    default: { dot: "bg-[#bfbfbf]", text: "text-[#8c8c8c]" },
};

const StatusBadge = ({ status }) => {
    const styles = STATUS_STYLES[status.color] ?? STATUS_STYLES.default;

    return (
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${styles.text}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${styles.dot}`} />
            {status.label}
        </span>
    );
};

StatusBadge.propTypes = {
    status: PropTypes.shape({
        label: PropTypes.string,
        color: PropTypes.string,
    }).isRequired,
};

const ReservationDetailsHeader = ({
    resDetails,
    customerDetails,
    t,
    onOpenInvoice,
    canRefreshInvoice,
    onRefreshInvoice,
    isRefreshingInvoice,
    showReject,
    onRejectReservation,
    isRejecting,
}) => {
    const history = useHistory();
    const status = getReservationStatus(resDetails, t);
    const customerName = getCustomerName(resDetails, customerDetails);
    const createdLabel = formatCreatedMeta(resDetails?.createdAt || resDetails?.pickupDate);

    const invoiceMenu = {
        items: [
            { key: "view", icon: <FileTextOutlined />, label: t("invoice.view") },
            { key: "refresh", icon: <ReloadOutlined />, label: t("invoice.refresh") },
        ],
        onClick: ({ key }) => (key === "refresh" ? onRefreshInvoice?.() : onOpenInvoice()),
    };
    const invoiceButtonClass = `${ACTION_SIZE_LG} ${SECONDARY_ACTION} w-full sm:w-auto`;

    return (
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined className="text-base text-[#2F2F46]" />}
                    onClick={() => history.push("/reservations")}
                    aria-label={t("carSwapModal.back")}
                    className="!mt-0.5 !h-9 !w-9 shrink-0 !rounded-lg !p-0 hover:!bg-[#F2F2FA]"
                />

                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="fontbold text-xl leading-tight text-[#2F2F46]">#{resDetails?.id}</h1>
                        <StatusBadge status={status} />
                    </div>
                    {createdLabel ? (
                        <p className="fontRegular mt-1 text-sm text-[#7f8c8d]">
                            {t("upcomingDetails.createdMeta", { date: createdLabel, customer: customerName })}
                        </p>
                    ) : null}
                </div>
            </div>

            {/* Side by side on phones too, so the header stays short above the main banner. */}
            <div className="grid w-full min-w-0 grid-flow-col auto-cols-fr gap-2 sm:flex sm:w-auto sm:items-center">
                {showReject ? (
                    <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        loading={isRejecting}
                        disabled={isRejecting}
                        onClick={onRejectReservation}
                        className={`${ACTION_SIZE_LG} w-full !bg-white sm:w-auto`}>
                        {isRejecting
                            ? t("resDetails.rejectingReservation")
                            : t("resDetails.rejectReservation")}
                    </Button>
                ) : null}
                {canRefreshInvoice ? (
                    <Dropdown menu={invoiceMenu} trigger={["click"]} disabled={isRefreshingInvoice}>
                        <Button
                            icon={<FileTextOutlined />}
                            loading={isRefreshingInvoice}
                            className={invoiceButtonClass}>
                            {t("invoice.title")}
                            <DownOutlined className="text-xs" />
                        </Button>
                    </Dropdown>
                ) : (
                    <Button icon={<FileTextOutlined />} onClick={onOpenInvoice} className={invoiceButtonClass}>
                        {t("invoice.title")}
                    </Button>
                )}
            </div>
        </div>
    );
};

ReservationDetailsHeader.propTypes = {
    resDetails: PropTypes.object,
    customerDetails: PropTypes.object,
    t: PropTypes.func.isRequired,
    onOpenInvoice: PropTypes.func.isRequired,
    canRefreshInvoice: PropTypes.bool,
    onRefreshInvoice: PropTypes.func,
    isRefreshingInvoice: PropTypes.bool,
    showReject: PropTypes.bool,
    onRejectReservation: PropTypes.func,
    isRejecting: PropTypes.bool,
};

export default ReservationDetailsHeader;
